import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Send, MessageCircle, Loader2, Sparkles, Clock, Globe, User, ArrowRight,
  Phone, UserPlus, BookmarkPlus,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { BANK_INFO } from "@/data/services";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORE_KEY = "ca_chat_session_v1";

// Country dropdown for the intro form
const COUNTRIES = [
  "México", "Estados Unidos", "España", "Argentina", "Colombia", "Chile", "Perú",
  "Venezuela", "Ecuador", "Bolivia", "Paraguay", "Uruguay", "Guatemala", "Honduras",
  "El Salvador", "Nicaragua", "Costa Rica", "Panamá", "Rep. Dominicana", "Cuba",
  "Puerto Rico", "Brasil", "Canadá", "Otro",
];

// Random delay between 20 and 60 seconds before showing assistant reply
const randomDelay = () => 20000 + Math.floor(Math.random() * 40000);

export const ChatSection = () => {
  const { lang } = useLanguage();
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState(null);
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [messages, setMessages] = useState([]); // {role: "user"|"assistant", content}
  const [input, setInput] = useState("");
  const [starting, setStarting] = useState(false);
  const [thinking, setThinking] = useState(false);
  const [thinkingSince, setThinkingSince] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Persist session across reloads (only id + name + country + messages)
  useEffect(() => {
    if (user && !name) setName(user.name || user.username || "");
    if (user && !country) setCountry(user.country || "");
  }, [user]);  // eslint-disable-line

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORE_KEY) || "null");
      if (stored?.sessionId && stored?.messages) {
        setSessionId(stored.sessionId);
        setName(stored.name || "");
        setCountry(stored.country || "");
        setMessages(stored.messages);
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (sessionId) {
      localStorage.setItem(
        STORE_KEY,
        JSON.stringify({ sessionId, name, country, messages })
      );
    }
  }, [sessionId, name, country, messages]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, thinking]);

  const startChat = async (e) => {
    e.preventDefault();
    if (!name.trim() || !country.trim()) {
      toast.error(lang === "es" ? "Completa tu nombre y país" : "Fill name and country");
      return;
    }
    setStarting(true);
    try {
      const res = await axios.post(`${API}/chat/start`, {
        name: name.trim(),
        country: country.trim(),
        lang,
      });
      setSessionId(res.data.session_id);
      setMessages([{ role: "assistant", content: res.data.message }]);
      setTimeout(() => inputRef.current?.focus(), 300);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          (lang === "es" ? "Error iniciando la consulta" : "Error starting consultation")
      );
    } finally {
      setStarting(false);
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || thinking) return;

    setMessages((m) => [...m, { role: "user", content: text }]);
    setInput("");
    setThinking(true);
    setThinkingSince(Date.now());

    try {
      // Fire request immediately but wait at least randomDelay before showing the reply
      const delay = randomDelay();
      const [reply] = await Promise.all([
        axios
          .post(`${API}/chat/message`, { session_id: sessionId, message: text })
          .then((r) => r.data.reply),
        new Promise((r) => setTimeout(r, delay)),
      ]);
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      toast.error(
        lang === "es" ? "Hubo un problema, intenta de nuevo" : "Something went wrong, try again"
      );
    } finally {
      setThinking(false);
      setThinkingSince(null);
    }
  };

  const resetChat = () => {
    localStorage.removeItem(STORE_KEY);
    setSessionId(null);
    setMessages([]);
    setName("");
    setCountry("");
    setInput("");
  };

  const copy = {
    eyebrow: lang === "es" ? "Consulta Espiritual" : "Spiritual Consultation",
    title: lang === "es" ? "Habla directamente conmigo" : "Speak directly with me",
    subtitle:
      lang === "es"
        ? "Cuéntame qué te pasa. Te escucho con mi don y te guío para resolver tu situación."
        : "Tell me what's happening. I listen with my gift and guide you to resolve your situation.",
    introTitle: lang === "es" ? "Antes de empezar" : "Before we start",
    introSubtitle:
      lang === "es"
        ? "Necesito saber con quién hablo. Es totalmente confidencial."
        : "I need to know who I'm speaking with. It's completely confidential.",
    name: lang === "es" ? "Tu nombre" : "Your name",
    country: lang === "es" ? "Tu país" : "Your country",
    countryPlaceholder: lang === "es" ? "Selecciona tu país" : "Select your country",
    startBtn: lang === "es" ? "Iniciar Consulta" : "Start Consultation",
    placeholder:
      lang === "es"
        ? "Escribe tu mensaje aquí..."
        : "Write your message here...",
    send: lang === "es" ? "Enviar" : "Send",
    notice:
      lang === "es"
        ? "Las respuestas pueden demorar hasta 1 minuto. Estoy contigo, dame un momento."
        : "Responses may take up to 1 minute. I'm with you, give me a moment.",
    typing: lang === "es" ? "Cristopher está escribiendo..." : "Cristopher is typing...",
    newChat: lang === "es" ? "Nueva consulta" : "New consultation",
    you: lang === "es" ? "Tú" : "You",
  };

  return (
    <section
      id="consulta"
      className="relative py-24 lg:py-32 px-6 lg:px-12 overflow-hidden"
      data-testid="chat-section"
    >
      <div className="absolute top-1/2 right-1/4 w-[500px] h-[500px] rounded-full bg-[#d4af37]/8 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-[#5A0001]/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10"
        >
          <div className="inline-flex items-center gap-2 text-[#d4af37] mb-4">
            <div className="w-12 h-px bg-[#d4af37]/40" />
            <Sparkles className="w-4 h-4" />
            <span className="uppercase tracking-[0.3em] text-xs">{copy.eyebrow}</span>
            <Sparkles className="w-4 h-4" />
            <div className="w-12 h-px bg-[#d4af37]/40" />
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-3">
            {copy.title}
          </h2>
          <p className="text-neutral-400 text-lg max-w-2xl mx-auto italic">
            {copy.subtitle}
          </p>
        </motion.div>

        {/* INTRO FORM */}
        {!user ? (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-dark border border-[#d4af37]/40 rounded-2xl p-8 lg:p-10 text-center"
            data-testid="chat-login-gate"
          >
            <Sparkles className="w-10 h-10 text-[#d4af37] mx-auto mb-3" />
            <h3 className="font-serif text-2xl text-white mb-2">
              {lang === "es" ? "Regístrate para hablar conmigo" : "Sign up to talk with me"}
            </h3>
            <p className="text-sm text-neutral-400 mb-5 max-w-md mx-auto">
              {lang === "es"
                ? "Crear tu cuenta es gratis. Tendrás acceso a la consulta espiritual, podrás dar me gusta a testimonios, recibir notificaciones y agendar citas. Tu información es 100% privada."
                : "Creating your account is free. You'll get access to spiritual consultation, like testimonials, receive notifications and schedule appointments. 100% private."}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/registro" data-testid="chat-cta-register" className="btn-gold px-7 py-3.5 rounded-full uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center gap-2">
                <UserPlus className="w-4 h-4" /> {lang === "es" ? "Crear Cuenta" : "Sign Up"}
              </Link>
              <Link to="/ingresar" className="btn-outline-gold px-7 py-3.5 rounded-full uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center">
                {lang === "es" ? "Ya tengo cuenta" : "I have account"}
              </Link>
            </div>
          </motion.div>
        ) : !sessionId ? (
          <motion.form
            onSubmit={startChat}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass-dark border border-[#d4af37]/40 rounded-2xl p-8 lg:p-10"
            data-testid="chat-intro-form"
          >
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 mb-3">
                <MessageCircle className="w-6 h-6 text-[#d4af37]" />
              </div>
              <h3 className="font-serif text-2xl text-white mb-1">{copy.introTitle}</h3>
              <p className="text-sm text-neutral-400">{copy.introSubtitle}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              <div>
                <Label className="text-neutral-300 flex items-center gap-2 mb-2 text-xs uppercase tracking-widest">
                  <User className="w-3 h-3 text-[#d4af37]" /> {copy.name}
                </Label>
                <Input
                  data-testid="chat-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  maxLength={60}
                  className="bg-black/40 border-white/10 text-white"
                />
              </div>
              <div>
                <Label className="text-neutral-300 flex items-center gap-2 mb-2 text-xs uppercase tracking-widest">
                  <Globe className="w-3 h-3 text-[#d4af37]" /> {copy.country}
                </Label>
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger
                    data-testid="chat-country-select"
                    className="bg-black/40 border-white/10 text-white"
                  >
                    <SelectValue placeholder={copy.countryPlaceholder} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#d4af37]/30 text-white max-h-72">
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c} value={c}>
                        {c}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <button
              type="submit"
              data-testid="chat-start-btn"
              disabled={starting}
              className="btn-gold w-full py-4 rounded-full uppercase text-xs tracking-[0.25em] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {starting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />{" "}
                  {lang === "es" ? "Conectando..." : "Connecting..."}
                </>
              ) : (
                <>
                  {copy.startBtn} <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

            <p className="text-center text-[11px] text-neutral-500 mt-4 flex items-center justify-center gap-1.5">
              <Clock className="w-3 h-3 text-[#d4af37]" /> {copy.notice}
            </p>
          </motion.form>
        ) : (
          // CHAT WINDOW
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-dark border border-[#d4af37]/40 rounded-2xl overflow-hidden flex flex-col h-[640px]"
            data-testid="chat-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-black/40">
              <div className="flex items-center gap-3">
                <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-[#d4af37] to-[#5A0001] flex items-center justify-center font-serif text-black font-bold">
                  C
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-black" />
                </div>
                <div>
                  <div className="text-white font-medium text-sm flex items-center gap-1.5">
                    Cristopher{" "}
                    <Sparkles className="w-3 h-3 text-[#d4af37]" />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400">
                    {lang === "es" ? "En línea" : "Online"}
                  </div>
                </div>
              </div>
              <button
                onClick={resetChat}
                data-testid="chat-reset-btn"
                className="text-[10px] uppercase tracking-widest text-neutral-400 hover:text-[#d4af37] border border-white/10 hover:border-[#d4af37]/50 rounded-full px-3 py-1.5"
              >
                {copy.newChat}
              </button>
            </div>

            {/* Quick shortcuts */}
            <div className="flex flex-wrap gap-2 px-5 py-2 border-b border-white/5 bg-black/30">
              <a
                data-testid="chat-shortcut-whatsapp"
                href={BANK_INFO.whatsappLink}
                target="_blank"
                rel="noreferrer"
                className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25 transition-colors"
              >
                <MessageCircle className="w-3 h-3" /> {lang === "es" ? "WhatsApp directo" : "Direct WhatsApp"}
              </a>
              <a
                data-testid="chat-shortcut-call"
                href={`tel:${BANK_INFO.whatsapp.replace(/\s+/g, "")}`}
                className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/25 transition-colors"
              >
                <Phone className="w-3 h-3" /> {lang === "es" ? "Llamar" : "Call"}
              </a>
              <button
                data-testid="chat-shortcut-vcard"
                onClick={() => {
                  const vcard = [
                    "BEGIN:VCARD",
                    "VERSION:3.0",
                    "FN:CrisAngel Astral",
                    "N:Astral;CrisAngel;;;",
                    "ORG:CrisAngel Astral",
                    "TITLE:Vidente y Brujo",
                    `TEL;TYPE=CELL,VOICE:${BANK_INFO.whatsapp.replace(/\s+/g, "")}`,
                    `TEL;TYPE=WhatsApp:${BANK_INFO.whatsapp.replace(/\s+/g, "")}`,
                    `URL:${BANK_INFO.tiktok}`,
                    `URL:https://crisangelastral.com`,
                    "END:VCARD",
                  ].join("\n");
                  const blob = new Blob([vcard], { type: "text/vcard" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "CrisAngel-Astral.vcf";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                  toast.success(lang === "es" ? "Contacto descargado" : "Contact downloaded");
                }}
                className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-violet-500/15 border border-violet-500/40 text-violet-300 hover:bg-violet-500/25 transition-colors"
              >
                <BookmarkPlus className="w-3 h-3" /> {lang === "es" ? "Guardar contacto" : "Save contact"}
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-4 bg-[#0a0a0a]/60">
              {messages.map((m, i) => (
                <MessageBubble key={i} role={m.role} content={m.content} youLabel={copy.you} />
              ))}
              {thinking && <TypingBubble label={copy.typing} since={thinkingSince} />}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <form
              onSubmit={sendMessage}
              className="border-t border-white/10 bg-black/60 p-4"
            >
              <div className="flex items-center gap-2">
                <Input
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={thinking}
                  placeholder={copy.placeholder}
                  className="bg-black/40 border-white/10 text-white flex-1"
                />
                <button
                  type="submit"
                  data-testid="chat-send"
                  disabled={!input.trim() || thinking}
                  className="btn-gold rounded-full w-12 h-12 flex items-center justify-center disabled:opacity-50"
                  aria-label="Send"
                >
                  {thinking ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
              <p className="text-[11px] text-neutral-500 mt-2 flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-[#d4af37]" /> {copy.notice}
              </p>
            </form>
          </motion.div>
        )}
      </div>
    </section>
  );
};

const MessageBubble = ({ role, content, youLabel }) => {
  const isUser = role === "user";
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
          isUser
            ? "bg-[#d4af37]/15 border border-[#d4af37]/40 text-white rounded-br-sm"
            : "bg-black/60 border border-white/10 text-neutral-100 rounded-bl-sm"
        }`}
      >
        <div className={`text-[10px] uppercase tracking-widest mb-1 ${isUser ? "text-[#d4af37]" : "text-emerald-400"}`}>
          {isUser ? youLabel : "Cristopher"}
        </div>
        {content}
      </div>
    </motion.div>
  );
};

const TypingBubble = ({ label, since }) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!since) return;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - since) / 1000)), 1000);
    return () => clearInterval(t);
  }, [since]);

  return (
    <div className="flex justify-start">
      <div className="bg-black/60 border border-white/10 rounded-2xl rounded-bl-sm px-4 py-3 text-sm text-neutral-300">
        <div className="text-[10px] uppercase tracking-widest mb-1 text-emerald-400">Cristopher</div>
        <div className="flex items-center gap-2">
          <span className="flex gap-1">
            <span className="w-2 h-2 rounded-full bg-[#d4af37] animate-bounce" style={{ animationDelay: "0ms" }} />
            <span className="w-2 h-2 rounded-full bg-[#d4af37] animate-bounce" style={{ animationDelay: "150ms" }} />
            <span className="w-2 h-2 rounded-full bg-[#d4af37] animate-bounce" style={{ animationDelay: "300ms" }} />
          </span>
          <span className="text-xs italic text-neutral-400">
            {label} {elapsed > 0 ? `(${elapsed}s)` : ""}
          </span>
        </div>
      </div>
    </div>
  );
};
